import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus } from 'lucide-react'
import { AddUser } from './AddUser'
import Loader from '../../../../common/components/Loader'
import { User } from '../../../../common/models'
import { IColumn, Table } from '../../../../core/components/Table'
import { formatDateTime } from '../../../../core/constants/times'
import { useTableParams } from '../../../../core/hooks/table-params'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import { getUsers } from '../../../../core/services/users'
import { useAppSelector } from '../../../../store'
import { MessageBox } from '../../messages/MessageBox'
import { Pagination } from '../../Pagination'

export const SettingPermissionsUsersPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    sortBy,
    setSortBy,
    totalElements,
    setTotalElemenets,
  } = useTableParams()

  const { hasEditPermission } = useCheckPermission({
    page: 'PERMISSIONS_EDIT_USER',
  })

  const [items, setItems] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [addEditModal, setAddEditModal] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const { festivalName, editionId } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getUsers({
        page,
        size,
        sortBy,
      })
      setItems(response.data.content)
      setTotalElemenets(response.data.totalElements)
      setSize(response.data.size)
      setPage(response.data.pageNumber + 1)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    size,
    sortBy,
    setPage,
    setSize,
    setTotalElemenets,
    editionId,
    festivalName,
  ])

  useEffect(() => {
    getData()
  }, [getData])

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(items.map((one) => one.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const timer = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useRef<{ [key: string]: string | null }>({})

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }

      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      const newTimer = setTimeout(async () => {
        setLoading(true)
        try {
          // const params: InvitesPaginationParams = {
          //   page,
          //   size,
          //   startDate,
          //   endDate,
          // }
          // if (searchParams.current.redeemCode) {
          //   params.redeemCode = searchParams.current.redeemCode
          // }
          // const { data, status } = await getUsers(params)
          // if (status === 200) {
          //   setItems(data.content)
          //   setTotalElemenets(data.totalElements)
          // }
        } catch (err) {
          console.log(err)
        } finally {
          setLoading(false)
        }
      }, 2000)
      timer.current = newTimer
    },
    [page, size, sortBy],
  )

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: false,
      searchable: false,
      render: (row) => `${row.firstName} ${row.lastName}`,
    },
    {
      title: 'Email',
      accessor: 'email',
      sortable: false,
      searchable: false,
    },
    {
      title: 'User Group',
      accessor: 'userGroup',
      sortable: false,
      searchable: false,
    },
    {
      title: 'Last Activity',
      accessor: 'userGroup',
      sortable: false,
      searchable: false,
      render: (row) => formatDateTime(row.lastActivityDate),
    },
    {
      title: 'Active',
      accessor: 'active',
      sortable: false,
      render: (row) => <Checkbox checked={row.active} disabled />,
    },
    {
      title: 'Custom Permissions',
      accessor: 'customPermissions',
      sortable: false,
      render: (row) => <Checkbox checked={row.customPermissions} disabled />,
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <Link
          className="view text-accent-1 underline"
          to={`/settings/permissions/users/${festivalName}/${editionId}/view/${row.id}`}
        >
          View
        </Link>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Users"
        actions={
          <>
            {hasEditPermission && (
              <Button
                variant="secondary"
                className="w-11 h-11"
                onClick={() => {
                  setAddEditModal(true)
                }}
              >
                <Plus className="w-4 h-4 text-white shrink-0" />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title={'Users'} />
        <Table
          handleSearch={handleSearchParams}
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={items}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <AddUser
        visible={addEditModal}
        setVisible={() => {
          setAddEditModal(false)
        }}
        onSuccess={getData}
        disabled={!hasEditPermission}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
